import { useState, useEffect } from 'react';
import DocumentWallet from './DocumentWallet';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Badge from './Badge';
import StateBlock from './StateBlock';

const TABS = ['Overview', 'Work Orders', 'Invoices', 'AP Ledger', 'AR Ledger', 'Documents'];

export default function ContractorPanel({ contractorId, onClose, onEdit }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('Overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/contractors/${contractorId}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [contractorId]);

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="quick-panel">
        <div className="drawer-header">
          <div>
            <h3 className="drawer-title">{data?.full_name || 'Loading…'}</h3>
            {data && <div className="form-hint">{describePrimaryEmail(data)}</div>}
          </div>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>

        {loading && <StateBlock type="loading" />}

        {data && (
          <>
            <div className="panel-tabs">
              {TABS.map(t => (
                <button key={t} className={'panel-tab' + (tab === t ? ' active' : '')} onClick={() => setTab(t)}>
                  {t}
                </button>
              ))}
            </div>

            <div className="panel-body">
              <div className="panel-summary-line">
                <Badge variant={data.computed_status === 'active' ? 'success' : 'neutral'}>{data.computed_status === 'active' ? 'Active' : 'Inactive'}</Badge>
                {' · '}
                {data.work_orders.length} work order{data.work_orders.length !== 1 ? 's' : ''}
              </div>

              {tab === 'Overview' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14 }}>
                  <RelationshipSummary workOrders={data.work_orders} contractorSubtype={data.contractor_subtype} />
                  <ContactList contacts={data.contacts} fallbackEmail={data.email} fallbackPhone={data.phone} />
                  <Field label="Subtype" value={data.contractor_subtype} />
                  <Field label="Work Authorization" value={data.work_authorization} />
                  <Field label="Address" value={data.address} />
                  <Field label="LinkedIn" value={data.linkedin} />
                  <Field label="Notes" value={data.notes} />
                </div>
              )}

              {tab === 'Work Orders' && (
                data.work_orders.length === 0
                  ? <StateBlock type="empty" title="No work orders" message="This contractor has no work orders yet." />
                  : data.work_orders.map(wo => (
                    <div key={wo.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontWeight: 600 }}>{wo.client_wo_number}</div>
                      <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                        {wo.engagement_type.replace('_', ' ')} · {wo.client_name || wo.vendor_name} · <Badge variant={wo.status === 'active' ? 'success' : 'neutral'}>{wo.status}</Badge>
                      </div>
                    </div>
                  ))
              )}

              {tab === 'Invoices' && (
                <>
                  <SummaryCards
                    cards={[
                      { label: 'AR Billed', value: data.summary.total_ar_billed },
                      { label: 'AR Verified Received', value: data.summary.total_ar_verified_received },
                      { label: 'AP Billed', value: data.summary.total_ap_billed },
                      { label: 'AP Verified Paid', value: data.summary.total_ap_verified_paid },
                    ]}
                  />
                  {data.ar_invoices.slice(0, 5).map(inv => (
                    <InvoiceRow key={'ar' + inv.id} inv={inv} amountKey="amount" />
                  ))}
                  {data.ap_invoices.slice(0, 5).map(inv => (
                    <InvoiceRow key={'ap' + inv.id} inv={inv} amountKey="amount_to_pay" />
                  ))}
                </>
              )}

              {tab === 'AP Ledger' && <ContractorLedgerSummary contractorId={data.id} side="ap" />}

              {tab === 'AR Ledger' && <ContractorLedgerSummary contractorId={data.id} side="ar" />}

              {tab === 'Documents' && <DocumentWallet entityType="contractor" entityId={data.id} entityName={`${data.first_name} ${data.last_name}`} />}
            </div>

            <div className="panel-footer" style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => onEdit(data)}>Edit</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => navigate(`/contractors/${contractorId}`)}>View Full Profile →</button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</div>
      <div>{value || '—'}</div>
    </div>
  );
}

const ENGAGEMENT_LABELS = {
  contract_placement: 'Contract Placement',
  recurring_referral: 'Recurring Referral',
  fte_placement: 'FTE Placement',
};

// A clear, at-a-glance summary of who this contractor is CURRENTLY engaged
// with — answers "who's the client, who's the vendor, what's the status"
// without having to click into the separate Work Orders tab. Rules
// (DECISIONS "Work Order rules", active WOs only): at most 2 active
// Recurring Referrals (buyer + seller side), never alongside an active
// Contract Placement; FTE Placement never blocks or is blocked.
function RelationshipSummary({ workOrders, contractorSubtype }) {
  const activeWOs = (workOrders || []).filter(w => w.status === 'active');

  if (activeWOs.length === 0) {
    return (
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 2 }}>Current engagement</div>
        <div style={{ color: 'var(--muted)' }}>No active Work Order</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>Current engagement{activeWOs.length > 1 ? 's' : ''}</div>
      {activeWOs.map(wo => (
        <div key={wo.id} style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ marginBottom: 6 }}>
            <Badge variant="info">{ENGAGEMENT_LABELS[wo.engagement_type] || wo.engagement_type}</Badge>
            <span style={{ marginLeft: 8, fontSize: 13, color: 'var(--muted)' }}>{wo.client_wo_number}</span>
          </div>
          <div style={{ fontSize: 14, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div><span style={{ color: 'var(--muted)' }}>Client:</span> {wo.client_name || '—'}</div>
            {contractorSubtype === 'corp_to_corp' && wo.engagement_type === 'contract_placement' && (
              <div><span style={{ color: 'var(--muted)' }}>Vendor:</span> {wo.vendor_name || '—'}</div>
            )}
            {wo.engagement_type === 'contract_placement' && contractorSubtype !== 'corp_to_corp' && (
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Paid directly by TMV — no vendor ({contractorSubtype?.toUpperCase()})</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function SummaryCards({ cards }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
      {cards.map(c => (
        <div key={c.label} style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{c.label}</div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>${(c.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
        </div>
      ))}
    </div>
  );
}

function InvoiceRow({ inv, amountKey }) {
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
      <span>{inv.invoice_number}</span>
      <span>${(inv[amountKey] || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
    </div>
  );
}

// A contact's label is treated as "not the person's own" if it mentions
// a vendor/firm rather than something personal — this is a labeling
// convention set when the contact is added, not an automatic guess.
function isVendorSourced(label) {
  if (!label) return false;
  return /vendor|firm|company|official/i.test(label);
}

function describePrimaryEmail(data) {
  const primaryEmail = data.contacts?.find(c => c.contact_type === 'email' && c.is_primary);
  if (primaryEmail) {
    return isVendorSourced(primaryEmail.label)
      ? `${primaryEmail.value} (via vendor)`
      : primaryEmail.value;
  }
  if (data.email) return data.email;
  return 'No email on file';
}

function ContactList({ contacts, fallbackEmail, fallbackPhone }) {
  if (!contacts || contacts.length === 0) {
    return (
      <>
        <Field label="Email" value={fallbackEmail} />
        <Field label="Phone" value={fallbackPhone} />
      </>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>Emails & phone numbers</div>
      {contacts.map(c => (
        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
          <span style={{ fontSize: 13 }}>{c.value}</span>
          {c.label && (
            <span className={'badge ' + (isVendorSourced(c.label) ? 'badge-warning' : 'badge-neutral')}>
              {isVendorSourced(c.label) ? 'via vendor' : c.label}
            </span>
          )}
          {Boolean(c.is_primary) && <span className="badge badge-success">Primary</span>}
        </div>
      ))}
    </div>
  );
}

function ContractorLedgerSummary({ contractorId, side }) {
  const [ledger, setLedger] = useState(null);
  useEffect(() => {
    setLedger(null);
    api.get(`/contractors/${contractorId}/ledger/${side}`).then(r => setLedger(r.data));
  }, [contractorId, side]);

  if (!ledger) return <StateBlock type="loading" />;
  if (ledger.rows.length === 0) return <StateBlock type="empty" title="No ledger activity yet" message={`No ${side.toUpperCase()} invoices for this contractor yet.`} />;

  function fmt(n) { return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`; }
  const otherPartyKey = side === 'ap' ? 'vendor_name' : 'client_name';

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Ledger Balance</div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{fmt(ledger.summary.ledger_balance)}</div>
        </div>
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Verified Balance</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary)' }}>{fmt(ledger.summary.verified_balance)}</div>
        </div>
      </div>
      {ledger.rows.slice(-5).reverse().map((r, i) => (
        <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{r.invoice_number} — {r[otherPartyKey] || '—'}</span>
            <span>{fmt(r.amount)}</span>
          </div>
          {r.payment_date && (
            <div style={{ color: 'var(--muted)', display: 'flex', justifyContent: 'space-between' }}>
              <span>Paid {r.payment_date}</span>
              <Badge variant={r.verification_status === 'verified' ? 'success' : 'warning'}>{r.verification_status}</Badge>
            </div>
          )}
        </div>
      ))}
      <a href="/ledgers" style={{ display: 'block', textAlign: 'center', marginTop: 14, fontSize: 13 }}>
        Open full Ledgers page for complete history & export →
      </a>
    </div>
  );
}
